import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import { fromEvent, merge } from 'rxjs';
import { AppState } from '../../../../../../core/reducers';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { RelationshipCategoryEditComponent } from '../relationship-category-edit/relationship-category-edit.component';
import { RelationshipCategoryDataSource } from '../../../../../../core/lists/_data-sources/relationship-category.datasource';
import {
  RelationshipCategoryOnServerDeleted,
  RelationshipCategoryPageRequested,
} from '../../../../../../core/lists/_actions/relationship-category.actions';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  selector: 'kt-relationship-category-list',
  templateUrl: './relationship-category-list.component.html',
  styleUrls: ['./relationship-category-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelationshipCategoryListComponent implements OnInit, OnDestroy {
  dataSource: RelationshipCategoryDataSource;
  displayedColumns = ['name', 'entityType', 'actions'];
  searchBarColumns = ['searchCategory', 'searchType', 'searchActions'];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchCategoryInput = '';
  searchTypeInput = '';
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;
  typesList = ['DOCTOR', 'PATIENT', 'FACILITY', 'CLIENT', 'AFFILIATION'];

  constructor(
    public dialog: MatDialog,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadRelationshipCategoryList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadRelationshipCategoryList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new RelationshipCategoryDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadRelationshipCategoryList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadRelationshipCategoryList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new RelationshipCategoryPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchCategoryInput = appliedFilters.name;
        this.searchTypeInput = appliedFilters.type;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'id';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.name = this.searchCategoryInput;
      filter.type = this.searchTypeInput;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'id';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deleteCategory(_item: { id: any }) {
    const _title = 'Delete Category';
    const _description = 'ARe you sure you want to delete Category?';
    const _waitDescription = 'Deleting...';

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new RelationshipCategoryOnServerDeleted({ id: _item.id }));
      this.loadRelationshipCategoryList();
    });
  }

  editCategory(_item: any) {
    const dialogRef = this.dialog.open(RelationshipCategoryEditComponent, {
      data: { mode: 'UPDATE', _item },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadRelationshipCategoryList();
    });
  }

  createCategory() {
    const dialogRef = this.dialog.open(RelationshipCategoryEditComponent, {
      data: { mode: 'CREATE' },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadRelationshipCategoryList();
    });
  }

  reloadRelationshipCategoryList(): void {
    this.paginator.pageIndex = 0;
    this.loadRelationshipCategoryList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchCategoryInput = '';
    this.searchTypeInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadRelationshipCategoryList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadRelationshipCategoryList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadRelationshipCategoryList();
  }
}
